"use client";

import React, { createContext, useContext, useCallback, ReactNode } from "react";
import { toast } from "sonner";

interface ErrorDetails {
  message: string;
  code?: string | number;
  details?: string;
  type?: "session" | "connection" | "api" | "general";
}

interface GlobalErrorContextType {
  reportError: (error: Error | ErrorDetails | string, type?: ErrorDetails["type"]) => void;
}

const GlobalErrorContext = createContext<GlobalErrorContextType | undefined>(undefined);

interface GlobalErrorProviderProps {
  children: ReactNode;
}

export function GlobalErrorProvider({ children }: GlobalErrorProviderProps) {
  const reportError = useCallback((error: Error | ErrorDetails | string, type: ErrorDetails["type"] = "general") => {
    let errorDetails: ErrorDetails;

    // Parse different error formats
    if (typeof error === "string") {
      errorDetails = { message: error, type };
    } else if (error instanceof Error) {
      errorDetails = { message: error.message, type };
    } else {
      errorDetails = { ...error, type: error.type || type };
    }

    // Show appropriate toast based on error type
    const toastOptions = {
      duration: 5000,
      dismissible: true,
    };

    switch (errorDetails.type) {
      case "session":
        toast.error("Session Error", {
          description: errorDetails.details || errorDetails.message || "Unable to create or manage chat session",
          ...toastOptions,
        });
        break;

      case "connection":
        toast.error("Connection Error", {
          description: errorDetails.details || errorDetails.message || "Unable to connect to the service",
          ...toastOptions,
        });
        break;

      case "api":
        toast.error("API Error", {
          description:
            errorDetails.details || errorDetails.message || "An error occurred while communicating with the server",
          ...toastOptions,
        });
        break;

      default:
        toast.error("Error", {
          description: errorDetails.details || errorDetails.message || "An unexpected error occurred",
          ...toastOptions,
        });
        break;
    }

    // Log error for debugging
    console.error("Global Error:", errorDetails);
  }, []);

  const contextValue: GlobalErrorContextType = {
    reportError,
  };

  return <GlobalErrorContext.Provider value={contextValue}>{children}</GlobalErrorContext.Provider>;
}

export function useGlobalError(): GlobalErrorContextType {
  const context = useContext(GlobalErrorContext);
  if (context === undefined) {
    throw new Error("useGlobalError must be used within a GlobalErrorProvider");
  }
  return context;
}
